import { useEffect, useRef, useState } from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  ArrowUpRight,
  Phone,
  Mail,
  MapPin,
  X,
  Menu,
  Compass,
  Ruler,
  Layers,
  Box,
  Building2,
  Blocks,
  Hammer,
  ChevronDown,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BRAND_LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_skz-architects/artifacts/d9d7fa2b1ddb787d_SKZ%20logo.jpg";

const PROJECTS = [
  {
    id: 1,
    title: "The Vertex Tower",
    category: "Architecture & Masterplanning",
    year: "2026",
    location: "Urban Core",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_fde328f2-2381-409c-ba4b-f1a7c069ed45/artifacts/b405a2ff8d0b4b1a_2026-08-01.jpg",
    description:
      "A mixed-use high-rise anchored on a landscaped podium — perforated screens, cantilevered terraces and a civic promenade woven through the site.",
  },
  {
    id: 2,
    title: "Debra Civic Infrastructure",
    category: "Structural Engineering · BIM Structure",
    year: "2025",
    location: "Debra, WB",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_fde328f2-2381-409c-ba4b-f1a7c069ed45/artifacts/b6f31cf1d16c66a6_04.png",
    description:
      "Column grid, shear-wall placement and reinforcement scheduling for a civic block — detailed to LOD 400 for direct contractor coordination.",
  },
  {
    id: 3,
    title: "Apex Horizon Tower",
    category: "BIM Architecture · 3D Visualization",
    year: "2026",
    location: "Metro Waterfront",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_fde328f2-2381-409c-ba4b-f1a7c069ed45/artifacts/c7202d750f54b968_03.png",
    description:
      "Dual-view study — photoreal exterior render against the structural BIM twin — illustrating the load path from pile cap to crown.",
  },
  {
    id: 4,
    title: "Medinipur Skyline Development",
    category: "Project Planning · Urban Design",
    year: "2026",
    location: "West Medinipur",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_fde328f2-2381-409c-ba4b-f1a7c069ed45/artifacts/6dd56984873481de_2026-08-01.jpg",
    description:
      "Multi-tenant residential masterplan with sustainable daylighting analysis, podium parking integration and generous green buffers.",
  },
  {
    id: 5,
    title: "Commercial Showcase",
    category: "Architecture · 3D Visualization",
    year: "2025",
    location: "Mixed-use Block",
    url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_skz-architects/artifacts/4b7523efcc4996e2_02.jpeg",
    description:
      "A sculptural commercial facade with chamfered volumes, triangular glazing and a street-level activated plinth.",
  },
  {
    id: 6,
    title: "Residential Apartment Block",
    category: "Residential · Facade Design",
    year: "2025",
    location: "West Bengal",
    url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_skz-architects/artifacts/591d54972fb4733e_05.jpeg",
    description:
      "A five-storey residential block with punched openings, cantilevered balconies and warm masonry accents.",
  },
  {
    id: 7,
    title: "Master Bedroom Suite",
    category: "Interior Design",
    year: "2026",
    location: "Private Residence",
    url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_skz-architects/artifacts/e822c1f3edd6fdb8_07.jpeg",
    description:
      "A pastel-toned bedroom with sculpted ceiling detail, bespoke wardrobes and a quiet wallpaper backdrop.",
  },
  {
    id: 8,
    title: "Children's Bedroom",
    category: "Interior Design",
    year: "2026",
    location: "Private Residence",
    url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_skz-architects/artifacts/8c264a6a6795d505_08.jpeg",
    description:
      "A warm blush-pink children's room with slatted feature wall, arched door and a wallpapered reading nook.",
  },
  {
    id: 9,
    title: "Luxury Master Suite",
    category: "Interior Design",
    year: "2026",
    location: "Private Residence",
    url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_skz-architects/artifacts/d4cbc85e33a88254_09.jpeg",
    description:
      "A textured headboard wall, pendant lighting and a bronze-leaf gradient give the master suite a hushed hotel-grade calm.",
  },
];

const SERVICES = [
  { id: "architecture", title: "Architecture", Icon: Compass, note: "01", body: "Concept, masterplanning, facade & sustainable envelopes for residential, commercial and civic programs." },
  { id: "structural-engineering", title: "Structural Engineering", Icon: Ruler, note: "02", body: "Foundation design, seismic resistance, concrete & steel frame calculation and site safety certification." },
  { id: "interior-design", title: "Interior Design", Icon: Layers, note: "03", body: "Bespoke spatial layouts, ergonomic detailing, lighting schemes and curated material palettes." },
  { id: "3d-visualization", title: "3D Visualization", Icon: Box, note: "04", body: "Photoreal rendering, daylight simulation and walkthrough animation prior to breaking ground." },
  { id: "project-planning", title: "Project Planning", Icon: Hammer, note: "05", body: "Feasibility studies, BOQ, construction scheduling and regulatory clearance management." },
  { id: "bim-architecture", title: "BIM Architecture", Icon: Building2, note: "06", body: "LOD 200–400 architectural information modeling, parametric detailing and clash coordination." },
  { id: "bim-structure", title: "BIM Structure", Icon: Blocks, note: "07", body: "3D rebar detailing, steel fabrication models and digital twins ready for contractor hand-off." },
];

/* ---------- Smooth scroll (lenis) ---------- */
function useLenis() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);
}

/* ---------- SVG Monogram Logo ---------- */
const Monogram = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <rect width="64" height="64" rx="4" fill="transparent" stroke="rgba(255,255,255,0.14)" />
    <path d="M28 8 L36 8 L36 42 L28 42 Z" fill="#D87D4A" />
    <path d="M30 12 L34 12 M30 16 L34 16 M30 20 L34 20 M30 24 L34 24 M30 28 L34 28 M30 32 L34 32 M30 36 L34 36" stroke="#0E1013" strokeWidth="0.8" />
    <text x="12" y="56" fontFamily="IBM Plex Mono, monospace" fontSize="9" fill="#F4F3EF" letterSpacing="2">SKZ</text>
  </svg>
);

/* ---------- Navbar ---------- */
function Navbar({ onContact }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  const links = [
    { href: "#home", label: "Home", tid: "nav-link-home" },
    { href: "#philosophy", label: "Studio", tid: "nav-link-about" },
    { href: "#services", label: "Practice", tid: "nav-link-services" },
    { href: "#projects", label: "Work", tid: "nav-link-projects" },
    { href: "#contact", label: "Contact", tid: "nav-link-contact" },
  ];

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? "backdrop-blur-xl bg-[#0E1013]/80 border-b border-white/5" : "bg-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <a href="#home" data-testid="navbar-brand-logo" className="flex items-center gap-3 group">
            <img src={BRAND_LOGO} alt="SKZ Architects & Engineers" className="h-10 w-10 rounded-sm object-cover bg-white/90 p-[2px]" />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-serif text-[17px] text-[#F4F3EF] tracking-tight">SKZ</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.28em] text-[#A0A5AD] mt-1">
                Architects · Engineers
              </span>
            </div>
          </a>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <a
                key={l.tid}
                data-testid={l.tid}
                href={l.href}
                className="text-[12px] font-mono uppercase tracking-[0.22em] text-[#A0A5AD] hover:text-[#F4F3EF] transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={onContact}
              data-testid="navbar-contact-cta"
              className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D87D4A] text-[#0E1013] text-[12px] font-mono uppercase tracking-[0.18em] hover:bg-[#C85A32] transition"
            >
              Start a Project <ArrowUpRight size={14} />
            </button>
            <button
              onClick={() => setOpen(true)}
              data-testid="navbar-menu-button"
              className="md:hidden p-2 border border-white/10 rounded-sm text-[#F4F3EF]"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#0E1013]/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex justify-end p-6">
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="p-2 text-[#F4F3EF]">
                <X size={22} />
              </button>
            </div>
            <div className="px-10 flex flex-col gap-6">
              {links.map((l) => (
                <a
                  key={l.tid}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-serif text-4xl text-[#F4F3EF]"
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* ---------- Kinetic Hero ---------- */
function Hero({ onContact }) {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const lines = ["Architectural rigor.", "Structural precision.", "Spatial elegance."];

  return (
    <section id="home" ref={containerRef} className="relative min-h-screen pt-28 pb-20 overflow-hidden">
      {/* Blueprint grid */}
      <div className="absolute inset-0 blueprint-grid opacity-70" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0E1013]/60 to-[#0E1013]" />

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-end">
        {/* Left column — text */}
        <div className="lg:col-span-7 pt-12">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="flex items-center gap-3 mb-10"
          >
            <span className="h-[1px] w-10 bg-[#D87D4A]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D87D4A]">
              Debra Bazar · West Medinipur · WB
            </span>
          </motion.div>

          <h1 className="font-serif font-light tracking-tight leading-[0.95] text-[14vw] sm:text-[11vw] lg:text-[7.4vw] text-[#F4F3EF]">
            {lines.map((ln, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  className="line-inner"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ delay: 0.35 + i * 0.14, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                >
                  {ln}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.9 }}
            className="mt-10 max-w-xl text-[15px] text-[#A0A5AD] leading-relaxed"
          >
            SKZ Architects & Engineers is a design-and-build practice working between the drawing
            board and the construction site — from first sketch to final rebar, every project
            carries one authored line of thought.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={onContact}
              data-testid="hero-contact-cta"
              className="group inline-flex items-center gap-3 px-6 py-3.5 rounded-full bg-[#D87D4A] text-[#0E1013] text-[12px] font-mono uppercase tracking-[0.22em] hover:bg-[#C85A32] transition"
            >
              Commission a Project
              <ArrowUpRight size={16} className="transition-transform group-hover:-translate-y-[2px] group-hover:translate-x-[2px]" />
            </button>
            <a
              href="#projects"
              data-testid="hero-explore-projects-cta"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/15 text-[#F4F3EF] text-[12px] font-mono uppercase tracking-[0.22em] hover:border-[#D87D4A]/60 transition"
            >
              Selected Work
            </a>
          </motion.div>
        </div>

        {/* Right column — framed hero image with parallax */}
        <div className="lg:col-span-5 relative h-[420px] md:h-[520px] lg:h-[640px]">
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 clip-frame overflow-hidden border border-white/10"
            style={{ y: imageY }}
          >
            <motion.img
              src={PROJECTS[0].url}
              alt={PROJECTS[0].title}
              style={{ scale: imageScale }}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0E1013]/70 via-transparent to-transparent" />
          </motion.div>

          {/* Floating info tag */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9 }}
            className="absolute -bottom-4 left-4 right-16 bg-[#0E1013]/85 backdrop-blur-xl border border-white/10 p-5"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.26em] text-[#D87D4A]">Latest · 2026</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A0A5AD] mt-2">
                  {PROJECTS[0].category}
                </div>
              </div>
              <div className="font-mono text-[10px] text-[#A0A5AD]">N 22.4°<br/>E 87.4°</div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#A0A5AD]"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 1.6, repeat: Infinity }}>
          <ChevronDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
}

/* ---------- Studio Philosophy ---------- */
function Philosophy() {
  const stats = [
    { k: "27+", v: "Projects Delivered" },
    { k: "5 yrs", v: "In Practice" },
    { k: "350", v: "BIM Accuracy" },
    { k: "7", v: "Engineer Team" },
  ];
  return (
    <section id="philosophy" className="relative py-28 lg:py-40">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: "-80px" }}
          className="grid lg:grid-cols-12 gap-10"
        >
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D87D4A]">01 — Studio</span>
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#F4F3EF] text-balance">
              Buildings that <em className="text-[#D87D4A] not-italic font-serif">stand</em> — and quietly stand out.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-12 lg:border-l border-white/10 flex flex-col gap-6">
            <p className="text-[16px] text-[#A0A5AD] leading-relaxed max-w-2xl">
              We are an architecture and engineering practice rooted in Debra, West Medinipur.
              Our work moves fluidly between concept sketches, parametric BIM models and the
              realities of the pour: a single team authoring the drawing, the detail and the
              decision on site. The result is architecture that is quietly inevitable.
            </p>
            <p className="text-[16px] text-[#A0A5AD] leading-relaxed max-w-2xl">
              Every project is handled by senior principals — no file leaves the studio without
              structural logic, buildability and craft reconciled.
            </p>
          </div>
        </motion.div>

        {/* Stats bento */}
        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {stats.map((s, i) => (
            <motion.div
              key={s.v}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              viewport={{ once: true }}
              className="bg-[#0E1013] p-8 lg:p-10 flex flex-col gap-2"
            >
              <div className="font-serif text-5xl lg:text-6xl text-[#F4F3EF] leading-none">{s.k}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.26em] text-[#A0A5AD] mt-3">{s.v}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Services ---------- */
function Services() {
  return (
    <section id="services" className="relative py-28 lg:py-40 border-t border-white/5">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex items-end justify-between mb-14">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D87D4A] mb-4">02 — Practice</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F4F3EF] tracking-tight leading-[1.02] max-w-3xl">
              Seven disciplines, one authored line of thought.
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/8 border border-white/10">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.id}
              data-testid={`service-card-${s.id}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              viewport={{ once: true, margin: "-60px" }}
              className="group bg-[#0E1013] hover:bg-[#16191E] p-8 lg:p-10 min-h-[260px] relative overflow-hidden lift"
            >
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 border border-white/15 flex items-center justify-center text-[#D87D4A] group-hover:border-[#D87D4A]/60 transition-colors">
                  <s.Icon size={18} strokeWidth={1.3} />
                </div>
                <span className="font-mono text-[10px] text-[#A0A5AD] tracking-[0.26em]">{s.note}</span>
              </div>
              <h3 className="font-serif text-2xl lg:text-3xl text-[#F4F3EF] mt-10">{s.title}</h3>
              <p className="mt-3 text-[13.5px] text-[#A0A5AD] leading-relaxed">{s.body}</p>
            </motion.article>
          ))}

          {/* 8th tile — CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="bg-[#D87D4A] text-[#0E1013] p-8 lg:p-10 min-h-[260px] flex flex-col justify-between"
          >
            <div className="font-mono text-[11px] uppercase tracking-[0.3em]">Begin a brief</div>
            <div>
              <h3 className="font-serif text-3xl lg:text-4xl leading-none">Site visit on request.</h3>
              <a href="#contact" className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em]">
                Open contact <ArrowUpRight size={14} />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Marquee ---------- */
function Marquee() {
  const words = [
    "Structural Integrity",
    "BIM Precision",
    "Spatial Elegance",
    "Regulatory Compliance",
    "Sustainable Urbanism",
    "Civic Craft",
  ];
  const row = [...words, ...words, ...words];
  return (
    <section className="py-12 border-y border-white/5 bg-[#0E1013] overflow-hidden">
      <div className="relative">
        <div className="flex gap-14 whitespace-nowrap marquee-track">
          {row.map((w, i) => (
            <span key={i} className="flex items-center gap-14">
              <span className="font-serif italic text-5xl md:text-7xl text-[#F4F3EF]/95">{w}</span>
              <span className="w-2 h-2 rounded-full bg-[#D87D4A]" />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Projects ---------- */
function ProjectCard({ p, index, onOpen }) {
  const ref = useRef(null);
  const onMove = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    ref.current.style.setProperty("--x", `${((e.clientX - r.left) / r.width) * 100}%`);
    ref.current.style.setProperty("--y", `${((e.clientY - r.top) / r.height) * 100}%`);
  };
  // Bento spans — 9 projects tile cleanly across rows
  const spans = [
    "md:col-span-12",   // 0
    "md:col-span-7",    // 1
    "md:col-span-5",    // 2
    "md:col-span-5",    // 3
    "md:col-span-7",    // 4
    "md:col-span-4",    // 5
    "md:col-span-4",    // 6
    "md:col-span-4",    // 7
    "md:col-span-12",   // 8
  ];
  const span = spans[index] || "md:col-span-6";
  const h = index === 0 || index === 8 ? "h-[420px] md:h-[560px]" : "h-[300px] md:h-[380px]";
  return (
    <motion.button
      ref={ref}
      onMouseMove={onMove}
      onClick={() => onOpen(p)}
      data-testid={`project-card-${p.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      viewport={{ once: true, margin: "-80px" }}
      className={`spotlight group relative text-left overflow-hidden border border-white/10 bg-[#16191E] ${span} ${h}`}
    >
      <img
        src={p.url}
        alt={p.category}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0E1013]/70 via-transparent to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-500" />
      <div className="relative h-full p-6 md:p-8 flex flex-col justify-between">
        <div className="flex items-start justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.26em] text-[#F4F3EF]/90 bg-black/40 backdrop-blur px-2.5 py-1">
            {p.category}
          </span>
          <span className="font-mono text-[10px] text-[#F4F3EF]/80 bg-black/30 backdrop-blur px-2 py-1">{p.year}</span>
        </div>
        <div className="flex items-end justify-between gap-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D87D4A]">
            0{p.id} · {p.location}
          </div>
          <div className="w-10 h-10 rounded-full border border-white/25 flex items-center justify-center text-[#F4F3EF] bg-black/30 backdrop-blur transition-all duration-500 group-hover:border-[#D87D4A] group-hover:bg-[#D87D4A] group-hover:text-[#0E1013]">
            <ArrowUpRight size={16} />
          </div>
        </div>
      </div>
    </motion.button>
  );
}

function ProjectModal({ project, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] bg-[#0E1013]/90 backdrop-blur-xl p-4 md:p-10 flex items-center justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-[1200px] w-full bg-[#16191E] border border-white/10 overflow-hidden grid lg:grid-cols-5 max-h-[90vh]"
      >
        <button
          onClick={onClose}
          data-testid="project-modal-close-button"
          className="absolute top-4 right-4 z-10 p-2 bg-black/50 border border-white/10 text-[#F4F3EF] hover:text-[#D87D4A]"
        >
          <X size={18} />
        </button>
        <div className="lg:col-span-3 h-[300px] md:h-[480px] lg:h-auto overflow-hidden">
          <img src={project.url} alt={project.title} className="w-full h-full object-cover" />
        </div>
        <div className="lg:col-span-2 p-8 lg:p-10 flex flex-col gap-6 overflow-y-auto">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D87D4A]">0{project.id} · {project.year}</div>
            <h3 className="font-serif text-4xl text-[#F4F3EF] mt-3 leading-tight">{project.title}</h3>
            <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#A0A5AD] mt-2">{project.category}</div>
          </div>
          <p className="text-[14px] text-[#A0A5AD] leading-relaxed">{project.description}</p>
          <div className="mt-auto border-t border-white/10 pt-6 grid grid-cols-2 gap-4">
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A0A5AD]">Location</div>
              <div className="font-serif text-xl text-[#F4F3EF] mt-1">{project.location}</div>
            </div>
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A0A5AD]">Year</div>
              <div className="font-serif text-xl text-[#F4F3EF] mt-1">{project.year}</div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Projects() {
  const [open, setOpen] = useState(null);
  return (
    <section id="projects" className="relative py-28 lg:py-40 border-t border-white/5">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D87D4A] mb-4">03 — Selected Work</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F4F3EF] tracking-tight leading-[1.02] max-w-3xl">
              Nine studies in rigor.
            </h2>
          </div>
          <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#A0A5AD] max-w-sm">
            Each project traces the full arc — brief, sketch, BIM, structure, build.
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-5 md:gap-6">
          {PROJECTS.map((p, i) => (
            <ProjectCard key={p.id} p={p} index={i} onOpen={setOpen} />
          ))}
        </div>
      </div>
      <AnimatePresence>
        {open && <ProjectModal project={open} onClose={() => setOpen(null)} />}
      </AnimatePresence>
    </section>
  );
}

/* ---------- Contact ---------- */
function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Your enquiry has reached the studio.", {
        description: "We'll reply to your email within one business day.",
      });
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 lg:py-40 border-t border-white/5">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        {/* Left — details */}
        <div className="lg:col-span-5">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D87D4A] mb-4">04 — Contact</div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F4F3EF] leading-[1.02] tracking-tight">
            Begin with a brief, a site, or a sketch.
          </h2>
          <p className="mt-6 text-[#A0A5AD] max-w-md leading-relaxed">
            Reach us directly — or share project details and we'll come back within one working day.
          </p>

          <div className="mt-12 flex flex-col gap-6">
            <a
              href="tel:+919064060049"
              data-testid="contact-phone-link"
              className="group flex items-start gap-5 pb-6 border-b border-white/10"
            >
              <div className="w-10 h-10 border border-white/15 flex items-center justify-center text-[#D87D4A] group-hover:border-[#D87D4A]/60 transition">
                <Phone size={16} strokeWidth={1.4} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A0A5AD]">Studio Line</div>
                <div className="font-serif text-2xl text-[#F4F3EF] mt-1">+91 90640 60049</div>
              </div>
            </a>
            <a
              href="mailto:skzarchengineers@gmail.com"
              data-testid="contact-email-link"
              className="group flex items-start gap-5 pb-6 border-b border-white/10"
            >
              <div className="w-10 h-10 border border-white/15 flex items-center justify-center text-[#D87D4A] group-hover:border-[#D87D4A]/60 transition">
                <Mail size={16} strokeWidth={1.4} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A0A5AD]">Correspondence</div>
                <div className="font-serif text-2xl text-[#F4F3EF] mt-1 break-all">skzarchengineers@gmail.com</div>
              </div>
            </a>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Debra+Bazar+West+Medinipur+West+Bengal"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-address-text"
              className="group flex items-start gap-5"
            >
              <div className="w-10 h-10 border border-white/15 flex items-center justify-center text-[#D87D4A] group-hover:border-[#D87D4A]/60 transition">
                <MapPin size={16} strokeWidth={1.4} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A0A5AD]">Studio Address</div>
                <div className="font-serif text-2xl text-[#F4F3EF] mt-1">Debra Bazar, West Medinipur</div>
                <div className="font-serif text-[18px] text-[#A0A5AD]">West Bengal, India</div>
              </div>
            </a>
          </div>
        </div>

        {/* Right — form */}
        <form
          onSubmit={submit}
          className="lg:col-span-7 bg-[#16191E] border border-white/10 p-8 lg:p-12"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D87D4A] mb-8">Project Enquiry</div>
          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Name" required>
              <input
                data-testid="contact-form-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="w-full bg-transparent border-b border-white/15 py-3 text-[15px] text-[#F4F3EF] placeholder:text-[#5c6168] focus:border-[#D87D4A] outline-none transition"
                placeholder="Your full name"
              />
            </Field>
            <Field label="Email" required>
              <input
                data-testid="contact-form-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="w-full bg-transparent border-b border-white/15 py-3 text-[15px] text-[#F4F3EF] placeholder:text-[#5c6168] focus:border-[#D87D4A] outline-none transition"
                placeholder="you@domain.com"
              />
            </Field>
            <Field label="Phone">
              <input
                data-testid="contact-form-phone-input"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full bg-transparent border-b border-white/15 py-3 text-[15px] text-[#F4F3EF] placeholder:text-[#5c6168] focus:border-[#D87D4A] outline-none transition"
                placeholder="+91 ·· ·····"
              />
            </Field>
            <Field label="Service">
              <select
                data-testid="contact-form-service-select"
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                className="w-full bg-transparent border-b border-white/15 py-3 text-[15px] text-[#F4F3EF] focus:border-[#D87D4A] outline-none transition appearance-none"
              >
                <option value="" className="bg-[#16191E]">Select a discipline</option>
                {SERVICES.map((s) => (
                  <option key={s.id} value={s.title} className="bg-[#16191E]">
                    {s.title}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="mt-6">
            <Field label="Project brief" required>
              <textarea
                data-testid="contact-form-message-textarea"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                rows={5}
                className="w-full bg-transparent border-b border-white/15 py-3 text-[15px] text-[#F4F3EF] placeholder:text-[#5c6168] focus:border-[#D87D4A] outline-none transition resize-none"
                placeholder="Site, scale, intent, timelines…"
              />
            </Field>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-form-submit-button"
            className="mt-10 inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-[#D87D4A] text-[#0E1013] text-[12px] font-mono uppercase tracking-[0.24em] hover:bg-[#C85A32] transition disabled:opacity-50"
          >
            {loading ? "Sending…" : "Send Enquiry"}
            <ArrowUpRight size={14} />
          </button>
        </form>
      </div>
    </section>
  );
}

const Field = ({ label, required, children }) => (
  <label className="block">
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A0A5AD] mb-1">
      {label} {required && <span className="text-[#D87D4A]">*</span>}
    </div>
    {children}
  </label>
);

/* ---------- Footer ---------- */
function Footer() {
  return (
    <footer className="border-t border-white/5 py-16">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid md:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-3">
            <img src={BRAND_LOGO} alt="SKZ" className="h-10 w-10 rounded-sm object-cover bg-white/90 p-[2px]" />
            <div className="font-serif text-xl text-[#F4F3EF]">SKZ Architects & Engineers</div>
          </div>
          <p className="mt-5 text-[13px] text-[#A0A5AD] max-w-xs leading-relaxed">
            A design-and-build studio from Debra Bazar — architecture authored line by line.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D87D4A] mb-3">Studio</div>
          <a href="#philosophy" className="text-[14px] text-[#A0A5AD] hover:text-[#F4F3EF]">Philosophy</a>
          <a href="#services" className="text-[14px] text-[#A0A5AD] hover:text-[#F4F3EF]">Practice</a>
          <a href="#projects" className="text-[14px] text-[#A0A5AD] hover:text-[#F4F3EF]">Selected Work</a>
          <a href="#contact" className="text-[14px] text-[#A0A5AD] hover:text-[#F4F3EF]">Contact</a>
        </div>
        <div className="flex flex-col gap-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D87D4A] mb-3">Reach</div>
          <a href="tel:+919064060049" className="text-[14px] text-[#A0A5AD] hover:text-[#F4F3EF]">+91 90640 60049</a>
          <a href="mailto:skzarchengineers@gmail.com" className="text-[14px] text-[#A0A5AD] hover:text-[#F4F3EF] break-all">
            skzarchengineers@gmail.com
          </a>
          <span className="text-[14px] text-[#A0A5AD]">Debra Bazar, West Medinipur</span>
          <span className="text-[14px] text-[#A0A5AD]">West Bengal, India</span>
        </div>
      </div>
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 mt-12 pt-6 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A0A5AD]">
          Designed for Structural Excellence · N 22.4° · E 87.4°
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A0A5AD]">
          SKZ · Architects & Engineers
        </div>
      </div>
    </footer>
  );
}

/* ---------- Home Composition ---------- */
const Home = () => {
  useLenis();
  const scrollToContact = () => {
    const el = document.getElementById("contact");
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <div className="App grain min-h-screen bg-[#0E1013] text-[#F4F3EF]">
      <Toaster position="top-center" theme="dark" />
      <Navbar onContact={scrollToContact} />
      <Hero onContact={scrollToContact} />
      <Philosophy />
      <Services />
      <Marquee />
      <Projects />
      <ContactSection />
      <Footer />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
